import {
  AnchorButton,
  Classes,
  Intent,
  MenuItem,
  Position,
} from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import {
  ItemRendererProps,
  MultiSelect2,
  MultiSelectProps,
} from '@blueprintjs/select';
import classNames from 'classnames';
import * as React from 'react';
import { Link, NoResultsMenuItem, Text, Tooltip } from 'vet-bones/components';
import { escapeStringForRegExp, getIntentClassName } from 'vet-bones/utils';
import { selectItemPredicate } from 'vet-bones/utils/select';

import { EditableLabel } from 'src/components/EditableLabel';
import {
  TEST_IDS,
  getProjectMappingCompositeIdentifierItemTestId,
} from 'src/constants/testIds';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import {
  MappingIdentifier,
  MappingIdentifierType,
  Resource,
} from 'src/containers/ProjectDiagram/types/data';
import { createNewIdentifierDelimiterItemRenderer } from 'src/containers/ProjectMapping/createNewIdentifierDelimiterItemRenderer';
import { useCompositeIdentifierPreview } from 'src/containers/ProjectMapping/hooks/compositePrimaryIdentifiers';
import { useSuggestionContext } from 'src/containers/ProjectMapping/hooks/suggestion';
import { MultiSelectIdentifierItem } from 'src/containers/ProjectMapping/types';
import { projectSelector } from 'src/selectors/project';
import * as copy from 'src/templates/copy';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';
import { validateIri } from 'src/utils/validateIri';

type ProjectMappingPrimaryIdentifierMultiSelectProps = {
  allowIri?: boolean;
  classLocalName: string;
  currentItems: MultiSelectIdentifierItem[];
  currentIdentifier: MappingIdentifier;
  fieldItems: MultiSelectIdentifierItem[];
  onUpdateIri: (iri: string | null) => void;
  onRemove: MultiSelectProps<MultiSelectIdentifierItem>['onRemove'];
  onSelect: MultiSelectProps<MultiSelectIdentifierItem>['onItemSelect'];
  resourceData: Resource | null;
};

const itemRenderer = (
  item: MultiSelectIdentifierItem,
  { handleClick, modifiers }: ItemRendererProps
) => {
  if (!modifiers.matchesPredicate) {
    return null;
  }

  return (
    <MenuItem
      key={item.value}
      active={modifiers.active}
      onClick={handleClick}
      text={item.text}
    />
  );
};

export const ProjectMappingPrimaryIdentifierMultiSelect: React.VFC<
  ProjectMappingPrimaryIdentifierMultiSelectProps
> = ({
  allowIri,
  classLocalName,
  currentItems,
  currentIdentifier,
  fieldItems,
  onRemove,
  onSelect,
  resourceData,
  onUpdateIri,
}) => {
  const { projectId } = useRouterParams();
  const diagram = useDiagram();
  const { isLoading } = useSuggestionContext();

  const project = useReduxSelector(projectSelector(projectId));
  const dataNamespace = project?.dataNamespace || '';
  const DATA_NAMESPACE_PLACEHOLDER =
    copy.components.projectMapping.primaryIdentifier.dataIriNamespace;
  const DATA_NAMESPACE_PLACEHOLDER_MATCHER = React.useMemo(
    () => new RegExp(`^${escapeStringForRegExp(DATA_NAMESPACE_PLACEHOLDER)}`),
    [DATA_NAMESPACE_PLACEHOLDER]
  );
  const defaultBaseIri = `${dataNamespace}${classLocalName}:`;

  const isIriEdited = currentIdentifier.baseIri !== null;
  const [isShowingIri, setIsShowingIri] = React.useState(isIriEdited);
  const displayBaseIri = (currentIdentifier.baseIri || '').replace(
    new RegExp(`^${escapeStringForRegExp(dataNamespace)}`),
    DATA_NAMESPACE_PLACEHOLDER
  );
  let displayIri: string = displayBaseIri;
  if (currentIdentifier.baseIri === null) {
    displayIri =
      copy.components.projectMapping.primaryIdentifier.dataIriPlaceholder(
        classLocalName
      );
  } else if (currentIdentifier.baseIri === '') {
    displayIri = '';
  }

  const inputPlaceholder = React.useMemo(() => {
    return currentIdentifier.baseIri !== null
      ? ''
      : copy.components.projectMapping.primaryIdentifier.dataIriPlaceholder(
          classLocalName
        );
  }, [classLocalName, currentIdentifier]);

  const validateBaseIri = React.useCallback(
    (newIri: string) => {
      if (!diagram) {
        return '';
      }
      if (newIri === '') {
        return '';
      }

      const expandedIri = newIri.replace(
        DATA_NAMESPACE_PLACEHOLDER_MATCHER,
        dataNamespace
      );

      if (expandedIri === currentIdentifier.baseIri) {
        return '';
      }

      const validationError = validateIri(expandedIri, true);
      if (validationError) {
        return validationError;
      }

      return '';
    },
    [
      DATA_NAMESPACE_PLACEHOLDER_MATCHER,
      currentIdentifier,
      dataNamespace,
      diagram,
    ]
  );

  const preview = useCompositeIdentifierPreview({
    allowIri,
    identifier: currentIdentifier,
    resourceData,
  });
  const errorMessage =
    currentItems.length === 0 ||
    allowIri ||
    currentItems.some((item) => item.type === MappingIdentifierType.FIELD)
      ? ''
      : copy.components.projectMapping.primaryIdentifier.fieldRequired;

  // check if the tag is a delimiter tag so the props include minimal styling
  const arrayOfIsDelimiters = currentItems.map(
    (item) => item.type === MappingIdentifierType.DELIMITER
  );
  const getTagProps = (_v: React.ReactNode, index: number) => {
    const isDelimiter = arrayOfIsDelimiters[index];
    const hasAdjacentDelimiter =
      arrayOfIsDelimiters[index - 1] || arrayOfIsDelimiters[index + 1];
    const isFirstOrLastIndex = index === currentItems.length - 1 || index === 0;
    const allowDelete = isFirstOrLastIndex || hasAdjacentDelimiter;
    return {
      'data-testid': getProjectMappingCompositeIdentifierItemTestId(
        index.toString()
      ),
      className: classNames({
        [getIntentClassName('sd-delimiter')]: isDelimiter,
        'sd-delimiter--not-deletable': isDelimiter && !allowDelete,
      }),
    };
  };

  let tooltipContent = copy.components.projectMapping.primaryIdentifier.showIri;
  if (isIriEdited) {
    tooltipContent = copy.components.projectMapping.primaryIdentifier.editedIri;
  } else if (isShowingIri) {
    tooltipContent = copy.components.projectMapping.primaryIdentifier.hideIri;
  }

  return (
    <div
      className="sd-project-mapping-composite-identifier"
      data-testid={
        TEST_IDS.projectMapping.primaryIdentifier.composite.container
      }
    >
      <div className="sd-project-mapping-composite-identifier__row">
        <MultiSelect2
          createNewItemFromQuery={(query) => ({
            text: query,
            value: query,
            type: MappingIdentifierType.DELIMITER,
          })}
          createNewItemPosition="first"
          createNewItemRenderer={createNewIdentifierDelimiterItemRenderer(
            undefined,
            allowIri
          )}
          data-testid={TEST_IDS.projectMapping.primaryIdentifier.composite.tags}
          disabled={isLoading}
          fill
          itemPredicate={selectItemPredicate()(fieldItems)}
          itemRenderer={itemRenderer}
          items={fieldItems}
          noResults={
            <NoResultsMenuItem text={copy.components.defaults.noResults} />
          }
          onItemSelect={onSelect}
          onRemove={onRemove}
          placeholder={
            copy.components.projectMapping.primaryIdentifier.inputPlaceholder
          }
          popoverProps={{ minimal: true, popoverClassName: 'sd-popover-menu' }}
          resetOnSelect
          selectedItems={currentItems}
          tagInputProps={{
            intent: errorMessage ? Intent.DANGER : Intent.NONE,
            tagProps: getTagProps,
          }}
          tagRenderer={(item) => item.text}
        />
        <div>
          <Tooltip content={tooltipContent} position={Position.BOTTOM}>
            <AnchorButton
              active={isShowingIri}
              data-testid={
                TEST_IDS.projectMapping.primaryIdentifier.editableIriButton
              }
              disabled={isIriEdited || isLoading}
              icon={IconNames.CODE}
              minimal
              onClick={() => setIsShowingIri(!isShowingIri)}
            />
          </Tooltip>
        </div>
      </div>
      <div
        className="sd-project-mapping-composite-identifier__row sd-project-mapping-composite-identifier__row--preview"
        data-testid={TEST_IDS.projectMapping.primaryIdentifier.previewContainer}
      >
        <div className="sd-project-mapping-composite-identifier__row--preview-text">
          {!errorMessage && preview
            ? copy.components.projectMapping.primaryIdentifier.preview
            : ''}
          {isShowingIri ? (
            <EditableLabel
              canSetEmptyString
              className={classNames(
                Classes.MONOSPACE_TEXT,
                'sd-project-mapping-composite-identifier__row--preview-editable-text',
                {
                  'sd-project-mapping-composite-identifier__row--preview-editable-text__empty':
                    displayIri === '',
                }
              )}
              disabled={isLoading}
              errorIntent={Intent.DANGER}
              minWidth={20}
              onSetValue={(newIri) => {
                const formattedNewIri: string = newIri
                  .trim()
                  .replace(DATA_NAMESPACE_PLACEHOLDER_MATCHER, dataNamespace);

                // clear base iri if it would be same as default
                if (formattedNewIri === defaultBaseIri) {
                  onUpdateIri(null);
                } else {
                  onUpdateIri(formattedNewIri);
                }
              }}
              onValidate={validateBaseIri}
              placeholder={inputPlaceholder}
              value={displayIri}
            />
          ) : null}
          <Text
            className={isShowingIri ? Classes.MONOSPACE_TEXT : ''}
            data-testid={
              TEST_IDS.projectMapping.primaryIdentifier.composite.preview
            }
            ellipsize
          >
            {errorMessage ||
              preview ||
              copy.components.projectMapping.primaryIdentifier.emptyPreview}
          </Text>
        </div>
        {isShowingIri && !errorMessage && Boolean(preview) ? (
          <Link
            className="sd-project-mapping-composite-identifier__row--preview-reset"
            onClick={() => onUpdateIri(null)}
            testId={TEST_IDS.projectMapping.primaryIdentifier.resetButton}
          >
            <Text ellipsize>reset</Text>
          </Link>
        ) : (
          <div />
        )}
      </div>
    </div>
  );
};
